/**
 * features/burs/EmployerDetails.tsx
 * ------------------------------------------------------------------
 * BURS — employer details (name, TIN, address) printed on every
 * statutory return. Persisted on the existing hrPayrollSettings
 * singleton via the optional burs* fields, so the bursar sets the
 * employer TIN once. Renders embedded inside the BURS hub.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { Building2, Save } from 'lucide-react';
import { SoftCard, glassFieldClass } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useHrData } from '@/features/hr/useHrData';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { PAYROLL_SETTINGS_ID, DEFAULT_PAYROLL_SETTINGS } from '@/lib/hrPayroll';
import { resolveEmployerDetails } from '@/lib/bursForms';

export default function EmployerDetails() {
  const data = useHrData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const resolved = resolveEmployerDetails(data.payrollSettings);
  const [name, setName] = useState(resolved.name);
  const [tin, setTin] = useState(resolved.tin);
  const [address, setAddress] = useState(resolved.addressLines.join('\n'));
  const [save, setSave] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({
    show: false,
    status: 'saving',
    message: '',
  });

  // Re-seed the form once settings arrive (first snapshot may be empty).
  useEffect(() => {
    if (data.payrollSettings) {
      const r = resolveEmployerDetails(data.payrollSettings);
      setName(r.name);
      setTin(r.tin);
      setAddress(r.addressLines.join('\n'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.payrollSettings?.id]);

  async function onSave() {
    setSave({ show: true, status: 'saving', message: 'Saving employer details' });
    try {
      const patch = {
        bursEmployerName: name.trim(),
        bursEmployerTin: tin.trim(),
        bursEmployerAddress: address.split('\n').map((l) => l.trim()).filter(Boolean),
      };
      if (data.payrollSettings) {
        await hr.settings.update(PAYROLL_SETTINGS_ID, patch);
      } else {
        await hr.settings.set(PAYROLL_SETTINGS_ID, { ...DEFAULT_PAYROLL_SETTINGS, ...patch });
      }
      await recordHrAudit({
        action: 'burs.employerSettings.update',
        actor,
        subjectId: PAYROLL_SETTINGS_ID,
        after: { bursEmployerName: patch.bursEmployerName, bursEmployerTin: patch.bursEmployerTin },
      });
      setSave({ show: true, status: 'success', message: 'Employer details saved' });
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Save failed' });
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <SaveConfirmation
        show={save.show}
        status={save.status}
        message={save.message}
        onDismiss={() => setSave((s) => ({ ...s, show: false }))}
      />
      <SoftCard hover={false}>
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={18} className="text-[var(--accent-link,#2d5295)]" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Employer details</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          These print on the ITW-7, ITW-8 and ITW-10. The TIN is the school's BURS Tax Identification
          Number and is required on submitted returns.
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Employer name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={glassFieldClass('mt-1 w-full')} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Employer TIN</span>
            <input
              value={tin}
              onChange={(e) => setTin(e.target.value)}
              placeholder="e.g. C00012345678"
              className={glassFieldClass('mt-1 w-full')}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Address (one line each)</span>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={3}
              className={glassFieldClass('mt-1 w-full')}
            />
          </label>
        </div>
        <button
          onClick={onSave}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl"
        >
          <Save size={15} /> Save employer details
        </button>
      </SoftCard>
    </div>
  );
}
